import { useNavigate } from '@tanstack/react-router';
import {
  Cell,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  Row,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronDown, ChevronUp, Copy, ExternalLink } from 'lucide-react';
import { useRef } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { DataTablePagination } from '@/components/ui/data-table-pagination';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Document } from '@/features/case-list/api/types';
import { cn } from '@/lib/utils';

interface DocumentsTableProps {
  documents: Document[];
  totalCount: number;
  pageCount: number;
  pageSize: number;
  onPaginationChange: (pagination: PaginationState) => void;
  onSortingChange: (sorting: SortingState) => void;
  pagination: PaginationState;
  sorting: SortingState;
  isFetching: boolean;
  pageRange?: number;
  height?: number;
}

const defaultColumn: Partial<ColumnDef<unknown>> = {
  size: 160,
  minSize: 120,
  maxSize: 500,
};

// Define columns for TanStack Table
const columns: ColumnDef<Document>[] = [
  {
    accessorKey: 'sourcedId',
    header: 'Source ID',
    cell: ({ row }) => {
      const sourcedId = row.getValue('sourcedId') as string;

      return (
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs truncate max-w-[120px]">
            {sourcedId}
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent row click when copying ID
                    navigator.clipboard.writeText(sourcedId);
                    toast.success('Source ID copied to clipboard');
                  }}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Copy Source ID</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    },
  },
  {
    accessorKey: 'title',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="p-0 hover:bg-transparent"
        >
          Title
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp className="ml-2 h-4 w-4" />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown className="ml-2 h-4 w-4" />
          ) : null}
        </Button>
      );
    },
  },
  {
    accessorKey: 'creator',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="p-0 hover:bg-transparent"
        >
          Creator
          {column.getIsSorted() === 'asc' ? (
            <ChevronUp className="ml-2 h-4 w-4" />
          ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown className="ml-2 h-4 w-4" />
          ) : null}
        </Button>
      );
    },
  },

  {
    accessorKey: 'description',
    header: 'Description',
    cell: ({ row }) => {
      const description = row.getValue('description') as string;
      return <div className="truncate max-w-[200px]">{description || '-'}</div>;
    },
  },
  {
    accessorKey: 'url',
    header: 'Link',
    cell: ({ row }) => {
      const url = row.getValue('url') as string;
      return url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-blue-500 hover:text-blue-700"
          onClick={(e) => e.stopPropagation()} // Prevent row click when opening link
        >
          View <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        '-'
      );
    },
  },
];

export function DocumentsTable({
  documents,
  totalCount,
  pageCount,
  pageSize: _pageSize,
  onPaginationChange,
  onSortingChange,
  pagination,
  sorting,
  isFetching,
  pageRange = 2, // Default to 2 pages before and after
  height = 600,
}: DocumentsTableProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleRowClick = (documentId: string) => {
    navigate({
      to: '/admin/case-standards/$documentId',
      params: { documentId },
    });
  };

  const table = useReactTable({
    data: documents,
    columns: columns as ColumnDef<unknown>[],
    defaultColumn: defaultColumn as ColumnDef<unknown>,
    state: {
      sorting,
      pagination,
    },
    rowCount: totalCount,
    manualPagination: true, // for ssr pagination
    manualSorting: true,
    onSortingChange: (updater) => {
      // Handle both function and value updates
      const newSorting =
        typeof updater === 'function' ? updater(sorting) : updater;

      onSortingChange(newSorting);
    },
    onPaginationChange: (updater) => {
      // Handle both function and value updates
      const newPagination =
        typeof updater === 'function' ? updater(pagination) : updater;

      onPaginationChange(newPagination);
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    pageCount: pageCount,
  });

  const virtualizer = useVirtualizer({
    count: documents.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 42,
    overscan: 20,
  });

  const rows = table.getRowModel().rows;

  return (
    <>
      <div
        className="w-full flex flex-col"
        style={{
          overflow: rows.length ? 'auto' : 'hidden', // scrollable table container
          position: 'relative',
          height: `${height}px`,
        }}
        ref={parentRef}
      >
        <table className="w-full caption-bottom text-sm">
          <TableHeader
            className={cn(
              'grid sticky top-0 z-[1] border-b border-slate-100 text-[.8rem] tracking-tighter text-slate-600',
              'bg-white'
            )}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="flex w-full justify-around items-center"
              >
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="flex flex-1 whitespace-nowrap justify-start items-center"
                    style={{
                      minWidth: header.column.columnDef.minSize,
                      width: header.column.getSize(),
                      maxWidth: header.column.columnDef.maxSize,
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody
            style={{
              position: 'relative',
              display: 'grid',
              height: `${Math.max(virtualizer.getTotalSize(), height - 100)}px`, //tells scrollbar how big the table is,
            }}
          >
            {!rows.length && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No documents found.
                </TableCell>
              </TableRow>
            )}
            {rows?.length && (
              <>
                {virtualizer.getVirtualItems().map((virtualRow) => {
                  const row = rows[virtualRow.index] as Row<Document>;
                  if (!row) return null;
                  const documentId = row.original.sourcedId;

                  return (
                    <TableRow
                      key={row.id}
                      data-index={virtualRow.index}
                      ref={(node) => virtualizer.measureElement(node)}
                      style={{
                        position: 'absolute',
                        transform: `translateY(${virtualRow.start}px)`, //this should always be a `style` as it changes on scroll
                        width: '100%',
                      }}
                      className="flex w-full justify-around bg-white hover:bg-sky-200 cursor-pointer"
                      onClick={() => handleRowClick(documentId)}
                    >
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <DocumentTableCell
                            key={cell.id}
                            rowIndex={virtualRow.index}
                            cell={cell}
                            isHidden={false}
                          />
                        );
                      })}
                    </TableRow>
                  );
                })}
              </>
            )}
          </TableBody>
        </table>

        <DataTablePagination
          table={table}
          isFetching={isFetching}
          pageRange={pageRange}
        />
      </div>
    </>
  );
}

interface TableCellProps<TData> {
  rowIndex: number;
  cell: Cell<TData, any>;
  isHidden: boolean;
}

export function DocumentTableCell<TData>({
  rowIndex,
  cell,
  isHidden,
}: TableCellProps<TData>) {
  if (isHidden) {
    return null;
  }
  return (
    <td
      style={{
        display: 'flex',
        minWidth: cell.column.columnDef.minSize,
        width: cell.column.getSize(),
        maxWidth: cell.column.columnDef.maxSize,
      }}
      className={cn(
        'powerpath-table-cell flex-1 text-neutral-500 px-2 py-2 align-middle text-sm justify-start tracking-tight overflow-hidden',
        {
          'border-t border-slate-100': rowIndex !== 0,
        }
      )}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </td>
  );
}
